// Estados del vídeo del héroe (UI-SPEC §B.3), sin React ni DOM.
//
// El estado guarda solo hechos que trae un evento (el vídeo empezó, falló, el usuario pulsó algo).
// El entorno (hidratado, escritorio, movimiento reducido o ahorro de datos, en pantalla, pestaña
// visible) se lee en cada render y se pasa a los selectores: así ningún efecto lo copia a un estado
// que pueda quedarse viejo (`deuda-de-sync-sin-guarda` está en 0).

export type FaseVideo = 'espera' | 'reproduciendo' | 'respaldo';

export interface EstadoVideoHeroe {
    fase: FaseVideo;
    conSonido: boolean;
    sonidoEstrenado: boolean;
    reinicioPendiente: boolean;
    pausadoPorUsuario: boolean;
}

export interface EntornoVideoHeroe {
    hidratado: boolean;
    escritorio: boolean;
    /** Movimiento reducido o ahorro de datos: el vídeo nunca se reproduce. */
    bloqueado: boolean;
    enPantalla: boolean;
    pestanaVisible: boolean;
}

export type EventoVideoHeroe = {
    tipo: 'reproduciendo' | 'fallo' | 'tiempoAgotado' | 'alternarSonido' | 'reiniciado' | 'alternarPausa';
};

export type EstadoVisibleVideo = 'estatico' | FaseVideo;

export type ModoSubtitulos = 'disabled' | 'hidden' | 'showing';

export const ESTADO_INICIAL_VIDEO: EstadoVideoHeroe = {
    fase: 'espera',
    conSonido: false,
    sonidoEstrenado: false,
    reinicioPendiente: false,
    pausadoPorUsuario: false,
};

type Transicion = (estado: EstadoVideoHeroe) => EstadoVideoHeroe;

// Una vez en el respaldo el vídeo no se enseña nunca: un «playing» tardío no puede tapar la animación.
const alReproducir: Transicion = (e) => (e.fase === 'espera' ? { ...e, fase: 'reproduciendo' } : e);
const alFallar: Transicion = (e) => (e.fase === 'respaldo' ? e : { ...e, fase: 'respaldo' });
// Los 4 s solo cuentan antes del primer fotograma: un vídeo que ya se ha visto no cae por lentitud.
const alAgotarse: Transicion = (e) => (e.fase === 'espera' ? { ...e, fase: 'respaldo' } : e);

// La primera vez que se activa el sonido el vídeo vuelve al segundo 0, para que la narración empiece
// por su primera palabra; las siguientes no saltan (UI-SPEC §B.3). La animación del respaldo no suena.
const alAlternarSonido: Transicion = (e) => {
    if (e.fase !== 'reproduciendo') return e;
    const estreno = !e.sonidoEstrenado;
    return { ...e, conSonido: !e.conSonido, sonidoEstrenado: true, reinicioPendiente: e.reinicioPendiente || estreno };
};
const alReiniciar: Transicion = (e) => ({ ...e, reinicioPendiente: false });
// WCAG 2.2.2: la pausa vale para el vídeo y para la animación del respaldo; en espera no hay nada moviéndose.
const alAlternarPausa: Transicion = (e) => (e.fase === 'espera' ? e : { ...e, pausadoPorUsuario: !e.pausadoPorUsuario });

const TRANSICIONES: Record<EventoVideoHeroe['tipo'], Transicion> = {
    reproduciendo: alReproducir,
    fallo: alFallar,
    tiempoAgotado: alAgotarse,
    alternarSonido: alAlternarSonido,
    reiniciado: alReiniciar,
    alternarPausa: alAlternarPausa,
};

export function reducirVideoHeroe(estado: EstadoVideoHeroe, evento: EventoVideoHeroe): EstadoVideoHeroe {
    return TRANSICIONES[evento.tipo](estado);
}

function entornoActivo(x: EntornoVideoHeroe): boolean {
    return x.escritorio && !x.bloqueado && x.enPantalla && x.pestanaVisible;
}

export function intentaReproducir(e: EstadoVideoHeroe, x: EntornoVideoHeroe): boolean {
    return x.hidratado && entornoActivo(x) && !e.pausadoPorUsuario && e.fase !== 'respaldo';
}

export function videoVisible(e: EstadoVideoHeroe, x: EntornoVideoHeroe): boolean {
    return e.fase === 'reproduciendo' && !x.bloqueado;
}

/** La animación HTML de la Fase 17 solo corre en el respaldo (UI-SPEC §B.3). */
export function animarRespaldo(e: EstadoVideoHeroe, x: EntornoVideoHeroe): boolean {
    return e.fase === 'respaldo' && entornoActivo(x) && !e.pausadoPorUsuario;
}

/** El valor de `data-estado-video`. */
export function estadoVisible(e: EstadoVideoHeroe, x: EntornoVideoHeroe): EstadoVisibleVideo {
    return x.bloqueado || !x.escritorio ? 'estatico' : e.fase;
}

/** El grupo de controles (UI-SPEC §B.4): mientras algo se mueve o el usuario lo ha pausado. */
export function mostrarControles(e: EstadoVideoHeroe, x: EntornoVideoHeroe): boolean {
    return !x.bloqueado && x.escritorio && e.fase !== 'espera';
}

/** El botón de sonido solo con el vídeo: la animación del respaldo no tiene sonido. */
export function mostrarSonido(e: EstadoVideoHeroe, x: EntornoVideoHeroe): boolean {
    return mostrarControles(e, x) && e.fase === 'reproduciendo';
}

// 18-34 (el dueño, 2026-10-11, anula «solo con sonido» de UI-SPEC §B.3): subtítulos siempre que se ve el
// vídeo, también en el bucle mudo. En la espera la pista va `hidden` para que el .vtt ya esté cargado en
// el primer fotograma; sin escritorio, con bloqueo o en el respaldo queda `disabled` y el navegador no
// pide el .vtt (el móvil sigue sin bajar ni un byte del vídeo).
export function modoSubtitulos(e: EstadoVideoHeroe, x: EntornoVideoHeroe): ModoSubtitulos {
    if (videoVisible(e, x)) return 'showing';
    const esperaReproducible = e.fase === 'espera' && x.hidratado && x.escritorio && !x.bloqueado;
    return esperaReproducible ? 'hidden' : 'disabled';
}
